import { computed, effect, inject, Injectable } from '@angular/core';

import { AppUser, Location, UserRole, userLocationIds, userTenantIds } from '../models/hospitality';
import { HospitalityStoreService } from '../services/hospitality-store.service';
import { AuthService } from './auth.service';
import { extractPrimaryUserRole, normalizeUserRole } from './role.utils';

@Injectable({ providedIn: 'root' })
export class AccessControlService {
  private readonly auth = inject(AuthService);
  private readonly store = inject(HospitalityStoreService);

  readonly role = computed(() => {
    const authUser = this.auth.user();

    return extractPrimaryUserRole(authUser?.realmRoles) ?? normalizeUserRole(authUser?.role);
  });
  readonly currentAppUser = computed(() => {
    const authUser = this.auth.user();

    if (!authUser) {
      return null;
    }

    return (
      this.store.users().find(
        (user) =>
          user.id === authUser.id ||
          (!!user.email && !!authUser.email && user.email.toLowerCase() === authUser.email.toLowerCase())
      ) ?? null
    );
  });

  readonly visibleTenantIds = computed(() => {
    const role = this.role();

    if (!role) {
      return [];
    }

    if (role === 'platform-admin' || role === 'operations') {
      return this.store.tenants().map((tenant) => tenant.id);
    }

    const appUser = this.currentAppUser();

    if (appUser?.scopes.length) {
      return userTenantIds(appUser.scopes);
    }

    return this.auth.user()?.tenantIds ?? [];
  });

  readonly visibleLocationIds = computed(() => {
    const role = this.role();

    if (!role) {
      return [];
    }

    if (role === 'platform-admin' || role === 'operations') {
      return this.store.locations().map((location) => location.id);
    }

    const appUser = this.currentAppUser();

    if (appUser?.scopes.length) {
      return userLocationIds(appUser.scopes);
    }

    return this.auth.user()?.locationIds ?? [];
  });

  readonly visibleTenants = computed(() => {
    const allowedTenantIds = new Set(this.visibleTenantIds());

    return this.store.tenants().filter((tenant) => allowedTenantIds.has(tenant.id));
  });

  readonly visibleLocations = computed(() => {
    const role = this.role();
    const allowedTenantIds = new Set(this.visibleTenantIds());
    const allowedLocationIds = new Set(this.visibleLocationIds());
    const limitByLocation = !(role === 'platform-admin' || role === 'operations') && allowedLocationIds.size > 0;

    return this.store.locations().filter(
      (location) =>
        allowedTenantIds.has(location.tenantId) && (!limitByLocation || allowedLocationIds.has(location.id))
    );
  });

  readonly visibleUsers = computed(() => {
    const role = this.role();

    if (role === 'platform-admin' || role === 'operations') {
      return this.store.users();
    }

    const allowedTenantIds = new Set(this.visibleTenantIds());
    const allowedLocationIds = new Set(this.visibleLocationIds());
    const limitByLocation = allowedLocationIds.size > 0;

    return this.store.users().filter((user) => {
      if (user.role === 'platform-admin') {
        return false;
      }

      const tenantMatch = userTenantIds(user.scopes).some((tenantId) => allowedTenantIds.has(tenantId));
      const locationIds = userLocationIds(user.scopes);
      const locationMatch =
        !limitByLocation || locationIds.length === 0 || locationIds.some((locationId) => allowedLocationIds.has(locationId));

      return tenantMatch && locationMatch;
    });
  });

  readonly canAccessAdministration = computed(() => {
    const role = this.role();

    return role === 'platform-admin' || role === 'tenant-admin' || role === 'operations';
  });

  readonly canOperateReception = computed(
    () =>
      this.role() === 'platform-admin' ||
      this.role() === 'tenant-admin' ||
      this.role() === 'reception'
  );
  readonly canCreateTenants = computed(() => this.role() === 'platform-admin');
  readonly canManageTenants = computed(
    () => this.role() === 'platform-admin' || this.role() === 'tenant-admin'
  );
  readonly canManageLocations = computed(
    () => this.role() === 'platform-admin' || this.role() === 'tenant-admin'
  );
  readonly canManageUsers = computed(
    () => this.role() === 'platform-admin' || this.role() === 'tenant-admin'
  );
  readonly canManageUnits = computed(
    () =>
      this.role() === 'platform-admin' ||
      this.role() === 'tenant-admin' ||
      this.role() === 'operations'
  );
  readonly canViewVendorCatalogs = computed(
    () => this.role() === 'platform-admin' || this.role() === 'tenant-admin'
  );
  readonly canManageVendorCatalogs = computed(() => this.role() === 'platform-admin');

  readonly assignableUserRoles = computed<UserRole[]>(() =>
    this.role() === 'platform-admin'
      ? ['platform-admin', 'tenant-admin', 'reception', 'operations', 'tv-integration']
      : ['tenant-admin', 'reception', 'operations', 'tv-integration']
  );

  constructor() {
    effect(() => {
      if (this.store.loading()) {
        return;
      }

      const tenants = this.visibleTenants();
      const selectedTenantId = this.store.selectedTenantId();

      if (tenants.length && !tenants.some((tenant) => tenant.id === selectedTenantId)) {
        this.store.setTenant(tenants[0].id);
      }
    });

    effect(() => {
      if (this.store.loading()) {
        return;
      }

      const visibleLocations = this.visibleLocationsForTenant(this.store.selectedTenantId());
      const selectedLocationId = this.store.selectedLocationId();

      if (!visibleLocations.length) {
        return;
      }

      if (!visibleLocations.some((location) => location.id === selectedLocationId)) {
        this.store.setLocation(visibleLocations[0]?.id ?? '');
      }
    });
  }

  visibleLocationsForTenant(tenantId: string): Location[] {
    const allowedLocationIds = new Set(this.visibleLocations().map((location) => location.id));

    return this.store
      .locations()
      .filter((location) => location.tenantId === tenantId && allowedLocationIds.has(location.id));
  }

  canAccessTenant(tenantId: string): boolean {
    return this.visibleTenantIds().includes(tenantId);
  }

  canAccessLocation(locationId: string): boolean {
    return this.visibleLocationIds().includes(locationId);
  }

  canAccessUser(user: AppUser | null): boolean {
    if (!user) {
      return false;
    }

    if (this.role() === 'platform-admin' || this.role() === 'operations') {
      return true;
    }

    return this.visibleUsers().some((entry) => entry.id === user.id);
  }

  canAssignUserRole(role: UserRole): boolean {
    return this.assignableUserRoles().includes(role);
  }
}
